(ns ui.editor2.unsplash
  "Actions of the editor2 Unsplash image sidebar
   (`ui.editor2.sidebar-images`). The sidebar state lives in the store
   under `[:editor2 :unsplash]`:

   - `:query` the text of the search input
   - `:mode` `:popular` or `:search`, `:search-query` the submitted query
   - `:photos` the loaded photos of all loaded pages, `:page` the last
     loaded page, `:total-pages` (search) and `:batch-size` (the number of
     photos of the last page) tell whether more can be loaded
   - `:loading` `:initial` (a new list) or `:more` (the next page)
   - `:request` the running or last failed request, `:error` its failure
   - `:adding-id` the photo that is being added to the design
   - `:credit` the attribution of the last added photo, `:select-error`
     why the last photo could not be added"
  (:require [clojure.string :as str]
            [storrito.unsplash.core :as core]))

(def path
  [:editor2 :unsplash])

(def photos-per-page
  ;; An even number fills the rows of the two column grid:
  24)

(def popular-start-pages
  ;; The popular photos start at a random page, so that the sidebar does
  ;; not always show the same photos:
  50)

(def photo-height
  ;; The photo is requested 1920px high (the height of a story):
  1920)

(defn error-message
  "What the user reads about a failed request with the HTTP `status`."
  [status]
  (case (str status)
    "403" "Unsplash limits how many photos Storrito can load per hour. Please try again in a few minutes."
    "Unsplash could not be reached. Check your connection and try again."))

(def utm-params
  ;; Unsplash's API guidelines ask for these on every link to Unsplash:
  "utm_source=storrito&utm_medium=referral")

(defn with-utm
  [url]
  (when url
    (str url
         (if (str/includes? url "?") "&" "?")
         utm-params)))

(def unsplash-url
  (with-utm "https://unsplash.com/"))

(def license-url
  (with-utm "https://unsplash.com/license"))

(defn has-more?
  "Whether another page of photos can be loaded."
  [{:keys [mode photos page total-pages batch-size]}]
  (boolean
    (and (seq photos)
         (case mode
           :search (< page (or total-pages 0))
           :popular (= batch-size photos-per-page)
           false))))

(defn start-request
  [unsplash request]
  (-> unsplash
      (assoc :loading (:kind request)
             :request request)
      (dissoc :error)
      (cond-> (= :initial (:kind request))
        (-> (assoc :mode (:mode request)
                   :search-query (:query request))
            (dissoc :photos :page :total-pages :batch-size)))))

(defn- distinct-by-id
  "A transducer that keeps the first photo of each id, a key of the grid."
  []
  (fn [rf]
    (let [seen (volatile! #{})]
      (fn
        ([] (rf))
        ([result] (rf result))
        ([result photo]
         (if (contains? @seen (:id photo))
           result
           (do (vswap! seen conj (:id photo))
               (rf result photo))))))))

(defn add-result
  "Shows the `result` of the `request`: a new list, or the next page
   appended. Popular pages can overlap, a photo is only shown once."
  [unsplash request {:keys [photos page total-pages]}]
  (let [shown (if (= :more (:kind request))
                (:photos unsplash)
                [])
        shown-ids (set (map :id shown))]
    (-> unsplash
        (assoc :photos (into shown
                             (comp (remove (comp shown-ids :id))
                                   (distinct-by-id))
                             photos)
               :page page
               :total-pages total-pages
               :batch-size (count photos))
        (dissoc :loading :request))))

(defn fail-request
  [unsplash status]
  (-> unsplash
      (assoc :error {:status status})
      (dissoc :loading)))

(defn next-page-request
  [{:keys [mode search-query page]}]
  {:kind :more
   :mode mode
   :query search-query
   :page (inc page)})

(defn- update!
  [store f & args]
  (apply swap! store update-in path f args))

(defn- fetch-photos
  [{:keys [mode query page]}]
  (case mode
    :popular (core/get-popular-photos {:page page
                                       :per-page photos-per-page})
    ;; `unsplash-js` (v5) decodes the whole URL after building the query
    ;; string, hence the query is encoded once more here:
    :search (core/search-unsplash-photos {:query (js/encodeURIComponent query)
                                          :page page
                                          :per-page photos-per-page})))

(defn- scroll-top!
  "Scrolls the sidebar back to the top, so that a new list starts at its
   first photo."
  []
  (loop [el (some-> (js/document.getElementById "unsplash-search")
                    (.-parentElement))]
    (when el
      (if (pos? (.-scrollTop el))
        (.scrollTo el #js {:top 0
                           :behavior "smooth"})
        (recur (.-parentElement el))))))

(defn- request!
  "Loads the photos of the `request`. A request that a newer one
   overtook is ignored."
  [store request]
  (let [request (assoc request :id (random-uuid))
        current? (fn []
                   (= (:id request)
                      (get-in @store (conj path :request :id))))]
    (update! store start-request request)
    (when (= :initial (:kind request))
      (scroll-top!))
    (-> (fetch-photos request)
        (.then (fn [result]
                 (when (current?)
                   (update! store add-result request result)))
               (fn [error]
                 (js/console.warn "Unsplash request failed:" error)
                 (when (current?)
                   (update! store fail-request (:status error)))))
        (.catch (fn [error]
                  (js/console.error "Showing the Unsplash photos failed:" error)
                  (when (current?)
                    (update! store fail-request nil)))))))

(defn- popular-request
  []
  {:kind :initial
   :mode :popular
   :page (inc (rand-int popular-start-pages))})

(defn init!
  "Loads popular photos when the sidebar opens for the first time. A
   reopened sidebar keeps its photos."
  [store]
  (let [unsplash (get-in @store path)]
    (when-not (or (seq (:photos unsplash))
                  (:loading unsplash)
                  (:error unsplash))
      (request! store (popular-request)))))

(defn search!
  "Searches for the text of the search input, an empty search shows the
   popular photos again."
  [store]
  (let [query (str/trim (or (get-in @store (conj path :query)) ""))]
    (request! store
              (if (str/blank? query)
                (popular-request)
                {:kind :initial
                 :mode :search
                 :query query
                 :page 1}))))

(defn load-more!
  [store]
  (let [unsplash (get-in @store path)]
    (when (and (has-more? unsplash)
               (not (:loading unsplash)))
      (request! store (next-page-request unsplash)))))

(defn retry!
  "Runs the failed request again, or loads popular photos when there is
   no failed request."
  [store]
  (let [{:keys [request]} (get-in @store path)]
    (request! store (or (dissoc request :id)
                        (popular-request)))))

(defn credit
  "The attribution that Unsplash's API guidelines ask for."
  [image]
  {:name (or (get-in image ["user" "name"])
             (get-in image ["user" "username"]))
   :profile-url (or (with-utm (get-in image ["user" "links" "html"]))
                    unsplash-url)
   :instagram-username (or (get-in image ["user" "instagram_username"])
                           (get-in image ["user" "social" "instagram_username"]))
   :thumb (get-in image ["urls" "thumb"])})

(defn image-asset
  [image asset-uuid]
  {:db/id "unsplash-image-asset"
   :asset/uuid asset-uuid
   :asset/type :image
   :asset/uri (get-in image ["urls" "full"])
   :asset/name (str "image-" (get image "slug"))
   :asset/width (get image "width")
   :asset/height (get image "height")})

(defn- add-photo!
  [store json]
  (let [image (js->clj json)
        asset-uuid (random-uuid)
        dispatch! (:replicant/dispatch! @store)]
    (update! store (fn [unsplash]
                     (-> unsplash
                         (assoc :credit (credit image))
                         (dissoc :adding-id))))
    (-> (core/download-photo json)
        (.catch (fn [error]
                  (js/console.warn "Unsplash download tracking failed:" error))))
    (dispatch! {:actions [[:editor2/transact-asset [(image-asset image asset-uuid)]]
                          [:editor2/prepare-assets!]
                          [:editor2/add-asset-as-clip asset-uuid]]})))

(defn select!
  "Adds the photo as image clip to the design. Unsplash's API guidelines
   ask to trigger the download endpoint for every used photo, see
   `storrito.unsplash.core/download-photo`."
  [store photo]
  (when-not (get-in @store (conj path :adding-id))
    (update! store (fn [unsplash]
                     (-> unsplash
                         (assoc :adding-id (:id photo))
                         (dissoc :credit :select-error))))
    (let [still-adding? (fn []
                          ;; Loading another design replaces `:editor2`
                          ;; and thereby drops the `:adding-id`, the photo
                          ;; must then not end up in the other design:
                          (= (:id photo)
                             (get-in @store (conj path :adding-id))))
          fail! (fn [error]
                  (js/console.warn "Adding the Unsplash photo failed:" error)
                  (when (still-adding?)
                    (update! store (fn [unsplash]
                                     (-> unsplash
                                         (assoc :select-error "The photo could not be added. Please try again.")
                                         (dissoc :adding-id))))))]
      (-> (core/get-unsplash-photo (:id photo) nil photo-height)
          (.then (fn [json]
                   (when (still-adding?)
                     (add-photo! store json)))
                 fail!)
          (.catch fail!)))))

(def register
  [{:ui.action/kind :unsplash/init
    :ui.action/handler (fn [{:keys [store]}]
                         (init! store))}
   {:ui.action/kind :unsplash/search
    :ui.action/handler (fn [{:keys [store]}]
                         (search! store))}
   {:ui.action/kind :unsplash/load-more
    :ui.action/handler (fn [{:keys [store]}]
                         (load-more! store))}
   {:ui.action/kind :unsplash/retry
    :ui.action/handler (fn [{:keys [store]}]
                         (retry! store))}
   {:ui.action/kind :unsplash/select
    :ui.action/handler (fn [{:keys [store action]}]
                         (select! store (second action)))}
   {:ui.action/kind :unsplash/dismiss-credit
    :ui.action/handler (fn [{:keys [store]}]
                         (update! store dissoc :credit :select-error))}])

(comment
  (credit {"user" {"name" "Jane Doe"
                   "links" {"html" "https://unsplash.com/@jane"}
                   "social" {"instagram_username" "jane"}}})

  (has-more? {:mode :search :photos [{}] :page 1 :total-pages 3})
  )
